import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { PRIORITIES, TICKET_TYPE_LABEL, type TicketType } from '@veta-desk/domain';
import { Shell, VIEWS, type ViewId } from '../components/Shell';
import { PriorityBadge, StatusText, TypeBadge } from '../components/Badges';
import { SlaMeter } from '../components/SlaMeter';
import type { SlaView } from '@veta-desk/domain';
import { api, type Meta, type TicketList } from '../lib/api';
import { useSession } from '../lib/session';
import { relativeTime } from '../lib/format';

const FILTER_TYPES: TicketType[] = ['INC', 'SOL', 'REQ'];
const select = 'h-9 rounded-md border border-[#D6D3CD] bg-white px-2 text-sm text-[#3F3D39]';

export default function Inbox() {
  const { t } = useTranslation();
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const requested = params.get('view');
  const view: ViewId = (VIEWS as readonly string[]).includes(requested ?? '') ? (requested as ViewId) : 'open';
  const filters = {
    type: params.get('type') ?? '',
    priority: params.get('priority') ?? '',
    organizationId: params.get('organizationId') ?? '',
    groupId: params.get('groupId') ?? ''
  };
  const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const q = params.get('q')?.trim() ?? '';
  // "En riesgo" se ordena por vencimiento salvo que se pida otra cosa.
  const sort = (params.get('sort') ?? (view === 'atRisk' ? 'due' : 'updated')) === 'due' ? 'due' : 'updated';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tickets', view, activeFilters, q, sort],
    queryFn: () => api<TicketList>(`/tickets?${new URLSearchParams({ view, sort, ...activeFilters, ...(q ? { q } : {}) })}`),
    staleTime: 15_000,
    refetchInterval: 60_000 // el SLA avanza solo
  });
  const meta = useQuery({ queryKey: ['meta'], queryFn: () => api<Meta>('/meta'), staleTime: 5 * 60_000 });

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const tickets = data?.tickets ?? [];

  return (
    <Shell>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{q ? t('inbox.searchTitle', { q }) : t(`views.${view}`)}</h1>
            <p className="mt-1 text-sm text-muted">
              {q ? (
                <>
                  {t('inbox.searchSubtitle', { count: data?.search?.total ?? tickets.length })}
                  {' · '}<Link to="/" className="text-accent underline-offset-2 hover:underline">{t('inbox.clearSearch')}</Link>
                </>
              ) : t(sort === 'due' ? 'inbox.subtitleDue' : 'inbox.subtitle')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!q && (
              <>
                <label className="sr-only" htmlFor="f-sort">{t('inbox.sortLabel')}</label>
                <select id="f-sort" className={select} value={sort} onChange={e => setFilter('sort', e.target.value === (view === 'atRisk' ? 'due' : 'updated') ? '' : e.target.value)}>
                  <option value="updated">{t('inbox.sortUpdated')}</option>
                  <option value="due">{t('inbox.sortDue')}</option>
                </select>
              </>
            )}
            <label className="sr-only" htmlFor="f-type">{t('inbox.filterType')}</label>
            <select id="f-type" className={select} value={filters.type} onChange={e => setFilter('type', e.target.value)}>
              <option value="">{t('inbox.allTypes')}</option>
              {FILTER_TYPES.map(ty => <option key={ty} value={ty}>{TICKET_TYPE_LABEL[ty]}</option>)}
            </select>
            <label className="sr-only" htmlFor="f-priority">{t('inbox.filterPriority')}</label>
            <select id="f-priority" className={select} value={filters.priority} onChange={e => setFilter('priority', e.target.value)}>
              <option value="">{t('inbox.allPriorities')}</option>
              {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <label className="sr-only" htmlFor="f-org">{t('inbox.filterClient')}</label>
            <select id="f-org" className={select} value={filters.organizationId} onChange={e => setFilter('organizationId', e.target.value)}>
              <option value="">{t('inbox.allClients')}</option>
              {meta.data?.organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <label className="sr-only" htmlFor="f-group">{t('inbox.filterGroup')}</label>
            <select id="f-group" className={select} value={filters.groupId} onChange={e => setFilter('groupId', e.target.value)}>
              <option value="">{t('inbox.allGroups')}</option>
              {meta.data?.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        </div>

        {isLoading ? (
          <p role="status" className="py-10 text-center text-sm text-muted">{t('common.loading')}</p>
        ) : isError ? (
          <p role="alert" className="rounded-md bg-white p-6 text-sm text-[#8F1D1D]">{t('errors.generic')}</p>
        ) : tickets.length === 0 ? (
          <section className="flex flex-col items-center gap-3 rounded-lg border border-line bg-white px-6 py-16 text-center">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#0F5F59" strokeWidth="1.5" aria-hidden="true">
              <path d="M22 12h-6l-2 3h-4l-2-3H2" />
              <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
            <h2 className="text-lg font-semibold">{q ? t('inbox.searchEmpty', { q }) : t(Object.keys(activeFilters).length || view !== 'open' ? 'inbox.emptyFiltered' : 'inbox.emptyTitle')}</h2>
            <p className="max-w-md text-sm text-muted">{q ? t('inbox.searchEmptyBody') : t('inbox.emptyBody', { tenant: me?.tenantName || '' })}</p>
            <Link to="/tickets/new" className="mt-2 inline-flex h-10 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark">
              {t('shell.newTicket')}
            </Link>
          </section>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line bg-white">
            <table className="w-full min-w-[980px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted">
                  {['number', 'subject', 'client', 'type', 'status', 'sla', 'assignee', 'updated'].map(col => (
                    <th key={col} scope="col" className="border-b border-line px-2.5 py-3 font-semibold">{t(`inbox.col.${col}`)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tickets.map(tk => (
                  <tr key={tk.number} className="border-b border-[#EFEDE9] last:border-0 hover:bg-[#FAFAF8]">
                    <td className="whitespace-nowrap px-2.5 py-3 font-mono text-[13px]">
                      <Link to={`/tickets/${tk.number}`} className="text-accent underline-offset-2 hover:underline">{tk.number}</Link>
                    </td>
                    <td className="min-w-[180px] max-w-[280px] px-2.5 py-3">
                      <Link to={`/tickets/${tk.number}`} className="font-medium text-ink hover:underline">{tk.subject}</Link>
                      {tk.needsTriage && <span className="ml-1.5 inline-block rounded bg-[#DCE6F0] px-1.5 py-0.5 align-middle text-[10px] font-semibold text-[#28445E]">por correo · revisar</span>}
                      <div className="mt-0.5 text-xs text-muted">{tk.requesterName}</div>
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-3 text-[#3F3D39]">
                      {tk.organizationName}
                      {tk.initiativeCode && <div className="mt-0.5 font-mono text-xs text-muted">{tk.initiativeCode}</div>}
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-3"><span className="inline-flex items-center gap-1.5"><TypeBadge type={tk.type} /><PriorityBadge priority={tk.priority} /></span></td>
                    <td className="px-2.5 py-3"><StatusText status={tk.status} holdReason={tk.holdReason} /></td>
                    <td className="px-2.5 py-3"><SlaCell sla={tk.sla} response={tk.response} /></td>
                    <td className="whitespace-nowrap px-2.5 py-3 text-[#3F3D39]">
                      {tk.assigneeName ?? <span className="text-muted">{t('inbox.unassigned')}</span>}
                      {tk.groupName && <div className="mt-0.5 max-w-[160px] truncate text-xs text-muted" title={tk.groupName}>{tk.groupName}</div>}
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-3 text-muted">{relativeTime(tk.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}

/** En la bandeja se ve el reloj que vence primero; si es la primera respuesta, se rotula. */
function SlaCell({ sla, response }: { sla: SlaView; response?: SlaView }) {
  const showResponse = Boolean(response?.running && (!sla.running || response.remainingMs <= sla.remainingMs));
  return (
    <div className="flex flex-col items-start gap-0.5">
      <SlaMeter sla={showResponse ? response : sla} compact />
      {showResponse && <span className="text-[10px] uppercase tracking-wide text-muted">1.ª respuesta</span>}
    </div>
  );
}
